import { z } from "zod";
import {
  Button,
  TextAreaInput,
  TextInput,
} from "@monolith-forensics/monolith-ui";
import ButtonMenu from "./components/ButtonMenu.js";
import { useForm, zodResolver } from "@mantine/form";
import diffFormData from "./utils/diffFormData.js";
import shortUUID from "short-uuid";
import { FC } from "react";

interface DefaultGroupFormProps {
  defaultFormData?: defaultGroupData;
  buttonProps?: { cancelText?: string; submitText?: string };
  onSubmit: (data: any, diffData?: any) => void;
  onCancel: () => void;
}

export interface defaultGroupData {
  uuid?: string;
  name?: string;
  description?: string;
}

const GroupSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
});

const DefaultGroupForm: FC<DefaultGroupFormProps> = ({
  defaultFormData,
  buttonProps = {},
  onSubmit,
  onCancel,
}) => {
  const form = useForm<defaultGroupData>({
    mode: "uncontrolled",
    initialValues: {
      uuid: defaultFormData?.uuid || shortUUID.generate(),
      ...defaultFormData,
    },
    validate: zodResolver(GroupSchema),
  });

  const handleSubmit = async () => {
    const validateResult = form.validate();

    if (validateResult.hasErrors) {
      console.error(validateResult.errors);
      return;
    }

    const formData = form.getValues();

    const diffData = diffFormData(defaultFormData, formData);

    // call onSubmit with modeled data and diff data
    // diff data is used when the form is in edit context
    // diff may be used when we only want to update modified values
    onSubmit?.(formData, diffData);
  };

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  return (
    <>
      <TextInput
        label="Group Name"
        placeholder="Group Name"
        required
        key={form.key("name")}
        {...form.getInputProps("name")}
      />
      <TextAreaInput
        label="Description"
        placeholder="Enter group description"
        description={"Provide a brief description of the group."}
        key={form.key("description")}
        {...form.getInputProps("description")}
      />

      <ButtonMenu>
        <Button size="xs" variant="subtle" onClick={handleCancel}>
          {buttonProps.cancelText || "Cancel"}
        </Button>
        <Button
          size="xs"
          color="primary"
          variant="contained"
          onClick={handleSubmit}
        >
          {buttonProps.submitText || "Submit"}
        </Button>
      </ButtonMenu>
    </>
  );
};

export default DefaultGroupForm;
